import { useEffect, useRef, useState } from "react";
import { BASE_QUESTIONS, type AnswerOptionGroup } from "../shared/inquiry-flow";
import type { SiteConfig } from "./site-config";
import {
  completeInquiry,
  getAppMode,
  requestNextQuestion,
  sendAnalyticsLeave,
  startAnalyticsSession,
} from "./api";
import { BrandHeader } from "./components/BrandHeader";
import { ChatMessage } from "./components/ChatMessage";
import { Composer } from "./components/Composer";
import { ContactForm } from "./components/ContactForm";
import { Progress } from "./components/Progress";
import { SuccessScreen } from "./components/SuccessScreen";
import type {
  ChatMessageData,
  ContactDetails,
  InquiryAnswer,
  InquirySummary,
} from "./types";

function makeMessage(
  role: ChatMessageData["role"],
  content: string,
  questionKind?: ChatMessageData["questionKind"],
): ChatMessageData {
  return { id: crypto.randomUUID(), role, content, questionKind };
}

export default function App({ siteConfig }: { siteConfig: SiteConfig }) {
  const configuredFirst = siteConfig.questions?.[0];
  const firstQuestion = BASE_QUESTIONS[0];
  const initialQuestion = configuredFirst ?? { id: firstQuestion.id, question: firstQuestion.question, options: [...firstQuestion.answerOptions], position: 1 };
  const firstMessage: ChatMessageData = { id: "welcome", role: "assistant", content: initialQuestion.question, questionKind: "base" };
  const [demoMode, setDemoMode] = useState(false);
  const [messages, setMessages] = useState<ChatMessageData[]>([firstMessage]);
  const [answers, setAnswers] = useState<InquiryAnswer[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<{
    questionId: InquiryAnswer["questionId"];
    questionKind: InquiryAnswer["kind"];
    targetFieldKey?: string;
  } | null>({
    questionId: initialQuestion.id as InquiryAnswer["questionId"],
    questionKind: "base",
    targetFieldKey: configuredFirst?.acquisitionFieldKey ?? undefined,
  });
  const [answerOptions, setAnswerOptions] = useState<string[]>([
    ...initialQuestion.options,
  ]);
  const [answerOptionGroups, setAnswerOptionGroups] = useState<AnswerOptionGroup[]>(
    configuredFirst?.optionGroups?.length
      ? configuredFirst.optionGroups.map((group) => ({ ...group, options: [...group.options] }))
      : firstQuestion.answerOptionGroups.map((group) => ({ ...group, options: [...group.options] })),
  );
  const [answerPlaceholder, setAnswerPlaceholder] = useState<string>(
    "",
  );
  const [readyForContact, setReadyForContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [completion, setCompletion] = useState<{
    inquiryId: string;
    contact: ContactDetails;
    emailStatus: "sent" | "demo";
    summary?: InquirySummary;
  } | null>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const chatHeadingRef = useRef<HTMLHeadingElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionPromiseRef = useRef<Promise<string | null> | null>(null);
  const sessionCompletedRef = useRef(false);

  useEffect(() => {
    let active = true;
    void getAppMode()
      .then((mode) => {
        if (active) setDemoMode(mode === "demo");
      })
      .catch(() => {
        if (active) setDemoMode(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    chatHeadingRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (messages.length === 1) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const frame = window.requestAnimationFrame(() => {
      messageEndRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "nearest",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages.length, readyForContact]);

  useEffect(() => {
    if (sessionPromiseRef.current) return;
    const sessionPromise = startAnalyticsSession()
      .then(({ sessionId }) => {
        sessionIdRef.current = sessionId;
        return sessionId;
      })
      .catch(() => null);
    sessionPromiseRef.current = sessionPromise;
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      if (sessionIdRef.current && !sessionCompletedRef.current) {
        sendAnalyticsLeave(sessionIdRef.current);
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, []);

  async function sendAnswer(answer: {
    content: string;
    freeText: string;
    selectedOptions: string[];
    selectedOptionsByGroup?: Record<string, string[]>;
  }) {
    if (!currentQuestion) return;
    const flowAnswer: InquiryAnswer = {
      questionId: currentQuestion.questionId,
      kind: currentQuestion.questionKind,
      content: answer.content,
      freeText: answer.freeText,
      selectedOptions: answer.selectedOptions,
      selectedOptionsByGroup: answer.selectedOptionsByGroup,
      ...(currentQuestion.targetFieldKey ? { targetFieldKey: currentQuestion.targetFieldKey } : {}),
    };
    const nextAnswers = [...answers, flowAnswer];
    const userMessage = makeMessage("user", answer.content);
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setBusy(true);
    setError("");

    try {
      const sessionId = sessionPromiseRef.current
        ? await sessionPromiseRef.current
        : sessionIdRef.current;
      const response = await requestNextQuestion(nextMessages, nextAnswers, sessionId);
      setAnswers(nextAnswers);
      setReadyForContact(response.readyForContact);
      setCurrentQuestion(
        response.questionId && response.questionKind
          ? {
              questionId: response.questionId,
              questionKind: response.questionKind,
              targetFieldKey: response.targetFieldKey,
            }
          : null,
      );
      setAnswerOptions(response.answerOptions);
      setAnswerOptionGroups(response.answerOptionGroups ?? []);
      setAnswerPlaceholder(response.answerPlaceholder);
      if (response.question) {
        setMessages([
          ...nextMessages,
          makeMessage("assistant", response.question, response.questionKind),
        ]);
      }
    } catch (requestError) {
      setMessages(messages);
      setError(
        requestError instanceof Error ? requestError.message : "通信に失敗しました。",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitContact(contact: ContactDetails) {
    setBusy(true);
    setError("");
    try {
      const sessionId = sessionPromiseRef.current
        ? await sessionPromiseRef.current
        : sessionIdRef.current;
      const response = await completeInquiry(messages, answers, contact, sessionId);
      sessionCompletedRef.current = true;
      setCompletion({
        inquiryId: response.inquiryId,
        contact,
        emailStatus: response.emailStatus,
        summary: response.summary,
      });
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "送信に失敗しました。",
      );
    } finally {
      setBusy(false);
    }
  }

  if (completion) {
    return (
      <SuccessScreen
        inquiryId={completion.inquiryId}
        messages={messages}
        contact={completion.contact}
        emailStatus={completion.emailStatus}
        summary={completion.summary}
        siteConfig={siteConfig}
      />
    );
  }

  const firstDemoFollowupId = demoMode
    ? messages.find(
        (message) => message.role === "assistant" && message.questionKind === "followup",
      )?.id
    : undefined;

  return (
    <div className="page-shell chat-view">
      <BrandHeader siteConfig={siteConfig} />
      <main className="chat-main">
        <h1 className="sr-only" ref={chatHeadingRef} tabIndex={-1}>
          {siteConfig.companyName}相談フォーム
        </h1>
        <section className="conversation-panel" aria-label="お問い合わせチャット">
          <Progress
            currentQuestionId={currentQuestion?.questionId ?? null}
            readyForContact={readyForContact}
          />
          <div className="message-list">
            {messages.map((message) => (
              <ChatMessage
                message={message}
                demoMode={demoMode}
                showDemoNotice={message.id === firstDemoFollowupId}
                siteConfig={siteConfig}
                key={message.id}
              />
            ))}
            {busy && !readyForContact ? (
              <div
                className="typing"
                aria-label={`${siteConfig.companyName}が回答を作成中`}
              >
                <i /> <i /> <i />
              </div>
            ) : null}
            <div className="message-end" ref={messageEndRef} aria-hidden="true" />
          </div>
          <div ref={liveRegionRef} className="sr-only" aria-live="polite">
            {messages.at(-1)?.role === "assistant" ? messages.at(-1)?.content : ""}
          </div>
          {readyForContact ? (
            <ContactForm
              demoMode={demoMode}
              disabled={busy}
              onSubmit={submitContact}
            />
          ) : (
            <Composer
              key={messages.at(-1)?.id}
              disabled={busy}
              options={answerOptions}
              optionGroups={answerOptionGroups}
              placeholder={answerPlaceholder}
              onSend={sendAnswer}
            />
          )}
          {error ? <p className="error-message" role="alert">{error}</p> : null}
        </section>
      </main>
    </div>
  );
}
